import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Switch, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';
import { DefaultSetpointsOption, ProactiveEngineStateEnum } from '@/constants/enums';
import ConditionalToolTip from '@/utils/ConditionalTooltip';
import { EngineDeactivationReasonEnum } from '@/constants/engine-deactivation-reason';
import { ProactiveEngineActivationBoxContainer } from './styled';
import { validateProactiveEngineActivationBox } from './Utils';
import { EngineState } from './EngineState';
import { DeactivationReasonModal } from './DeactivationReasonModal';
import StationStore from '@/store/Station/StationStore';
import { EngineStore } from '@/store/Engine/EngineStore';
import PermissionStore from '@/store/PermissionStore';
import ProactiveEngineStore from '@/store/ProactiveEngineStore';
import UserStore from '@/store/User/UserStore';
import { ActivateDynamicControllerModal } from './ActivateDynamicControllerModal';
import { DeactivateDynamicControllerModal } from './DeactivateDynamicControllerModal';

const ProactiveEngineActivationBox = observer(() => {
  const {
    proactiveEngineStore,
    permissionStore,
    engineStore,
    stationStore,
    userStore,
  }: {
    proactiveEngineStore?: ProactiveEngineStore;
    permissionStore?: PermissionStore;
    engineStore?: EngineStore;
    stationStore?: StationStore;
    userStore?: UserStore;
  } = useStores();
  const { t } = useTranslation();
  const [isProactiveEngineStateActivated, setProactiveEngineStateActivated] = useState(
    proactiveEngineStore.proactiveEngineState === ProactiveEngineStateEnum.Activated,
  );

  const isLoading = proactiveEngineStore.isRetrievingDefaultSetpointsOption || proactiveEngineStore.isChangingState;
  const hasViewEnginePermissions = permissionStore.isAuth(permissions.VIEW_STATION_ENGINE);
  const [proactiveEngineStatusLabel, setProactiveEngineStatusLabel] = useState<string>();
  const [commentRef, setCommentRef] = useState<EngineDeactivationReasonEnum | null>(null);
  const [comment, setComment] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const popconfirmTitleRef = useRef<{ resetControls: () => void } | null>(null);
  const [isDeactivationModalOpen, setIsDeactivationModalOpen] = useState(false);
  const [isActivationModalVisible, setIsActivationModalVisible] = useState(false);
  const [isDeactivationModalVisible, setIsDeactivationModalVisible] = useState(false);
  const [defaultSetpointsOption, setDefaultSetpointsOption] = useState<DefaultSetpointsOption>(null);

  const [deactivateData, setDeactivateData] = useState({
    reason: engineStore?.engineStatus?.commentRef || '',
    comment: engineStore?.engineStatus?.comment || '',
    user: engineStore?.engineStatus?.userEmail || '',
    date: engineStore?.engineStatus?.startTime || null,
  });

  useEffect(() => {
    setProactiveEngineStateActivated(proactiveEngineStore.proactiveEngineState === ProactiveEngineStateEnum.Activated);
  }, [proactiveEngineStore.proactiveEngineState]);

  useEffect(() => {
    const fetchData = async () => {
      if (!stationStore?.currentStation?.id) return;
      await proactiveEngineStore.getProactiveEngineState();
      setDeactivateData({
        reason: engineStore?.engineStatus?.commentRef || '',
        comment: engineStore?.engineStatus?.comment || '',
        user: engineStore?.engineStatus?.userEmail || '',
        date: engineStore?.engineStatus?.startTime || null,
      });

      setProactiveEngineStatusLabel(proactiveEngineStore.getProactiveEngineStatusLabel(hasViewEnginePermissions, t));
    };
    fetchData();
  }, [engineStore?.engineStatus, proactiveEngineStore.engineStatus]);

  const activeUser = userStore?.user?.email;

  useEffect(() => {
    (async () => {
      setDefaultSetpointsOption(await proactiveEngineStore.getDefaultSetpoints());
    })();
  }, [stationStore.currentStation?.id]);

  const onConfirmChangeProactiveEngineState = async () => {
    try {
      // Only validate form when deactivating
      if (isProactiveEngineStateActivated) {
        setIsDeactivationModalVisible(false);
        if (!validateForm()) return;
      }

      // default setpoints should be hidden when activating and receiving a response before the modal closes

      setIsActivationModalVisible(false);
      await proactiveEngineStore.toggleState(commentRef, comment);
      resetModal();
    } catch (e) {
      // Determine the error message based on the error type
      let errorKey: string;

      if (e?.response?.status === 400 && e?.response?.data?.message?.includes('compressors being in maintenance')) {
        // Specific compressor maintenance error
        errorKey = 'CompressorInMaintenancePreventsActivation';
      } else if (e?.message === 'dynamicControlInvalidStationStage') {
        errorKey = 'dynamicControlInvalidStationStage';
      } else {
        // Generic activation/deactivation error
        errorKey = !isProactiveEngineStateActivated
          ? 'FailedToActivateDynamicController'
          : 'FailedToDeactivateDynamicController';
      }

      message.error(t(errorKey));
    }
  };

  const validateForm = () => {
    const isValid = validateProactiveEngineActivationBox(isProactiveEngineStateActivated, commentRef, comment);

    setDeactivateData({
      reason: commentRef ? EngineDeactivationReasonEnum[commentRef] : '',
      comment: comment || '',
      user: activeUser || '',
      date: new Date(),
    });

    setIsError(!isValid);
    return isValid;
  };

  const onSwitchClick = () => {
    if (isProactiveEngineStateActivated) {
      setIsDeactivationModalVisible(true);
      resetModal();
    } else {
      setIsActivationModalVisible(true);
      resetModal();
    }
  };

  const onCancelModal = () => {
    setIsActivationModalVisible(false);
    setIsDeactivationModalVisible(false);
    resetModal();
  };

  const resetModal = () => {
    setIsError(false);
    if (popconfirmTitleRef.current) {
      popconfirmTitleRef.current.resetControls();
    }
  };

  const isStationSwitchDisabled =
    !permissionStore.isAuth(permissions.UPDATE_STATION_CONTROL) || proactiveEngineStore.isProactiveEngineEditDisabled;

  return (
    <ConditionalToolTip
      content={t(proactiveEngineStatusLabel)}
      placement="left"
      condition={!!proactiveEngineStatusLabel}
      overlayClassName="tooltip-overlay"
    >
      <ProactiveEngineActivationBoxContainer>
        <div className="proactive-engine-state-box">
          <div className="engine-state-info-group">
            <span className="engine-mode-title">{t('Controller Mode')}</span>
            <EngineState />
          </div>

          <div className="change-engine-state">
            <Switch
              size="small"
              onClick={onSwitchClick}
              checked={isProactiveEngineStateActivated}
              loading={proactiveEngineStore.isRetrievingState || proactiveEngineStore.isChangingState}
              disabled={isStationSwitchDisabled}
            />

            <ActivateDynamicControllerModal
              isVisible={isActivationModalVisible}
              onOk={onConfirmChangeProactiveEngineState}
              onCancel={onCancelModal}
            />

            <DeactivateDynamicControllerModal
              isVisible={isDeactivationModalVisible}
              onOk={onConfirmChangeProactiveEngineState}
              onCancel={onCancelModal}
              isLoading={isLoading}
              isProactiveEngineStateActivated={isProactiveEngineStateActivated}
              popconfirmTitleRef={popconfirmTitleRef}
              setCommentRef={setCommentRef}
              setComment={setComment}
              setIsError={setIsError}
              defaultSetpointsOption={defaultSetpointsOption}
              isError={isError}
            />
          </div>
        </div>

        {!isProactiveEngineStateActivated && (
          <div className="deactivated-reason-container" onClick={() => setIsDeactivationModalOpen(true)}>
            {t('viewReason')}
          </div>
        )}
        <DeactivationReasonModal
          isOpen={isDeactivationModalOpen}
          setIsDeactivationModalOpen={setIsDeactivationModalOpen}
          deactivationData={deactivateData}
        />
      </ProactiveEngineActivationBoxContainer>
    </ConditionalToolTip>
  );
});

export default ProactiveEngineActivationBox;
